// Shared "fastest valid lap" selection with outlier / incomplete-lap rejection.
//
// SINGLE source of truth for best-lap selection on the client. A server-side
// port lives in server/bestLap.js — the two implement the SAME rules and MUST
// be kept in sync (CRA can only bundle from src/, so one literal shared module
// across both runtimes isn't possible).
//
// Rules:
//   - ignore laps with non-positive lapTime
//   - reject implausibly fast laps  (< 50% of the median lap time, e.g. a
//     0.2s GPS-glitch "lap")
//   - reject incomplete laps        (max distance < 75% of the median lap
//     distance — in/out laps, sessions that ended mid-lap)

// Median of each lap's maximum distance, used to detect incomplete laps.
export function getMedianLapDist(laps) {
  if (!laps?.length) return 0;
  const dists = laps
    .map((l) => (l.data?.length ? Math.max(...l.data.map((p) => p.dist || 0)) : 0))
    .filter((d) => d > 0);
  if (!dists.length) return 0;
  const sorted = [...dists].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
}

// Index of the fastest *complete* lap. Falls back to 0 when nothing qualifies.
export function findBestLapIndex(laps, medianLapDist = getMedianLapDist(laps)) {
  if (!laps?.length) return 0;
  const times = laps.map((l) => l.lapTime).filter((t) => t > 0);
  const sortedTimes = [...times].sort((a, b) => a - b);
  const medianTime = sortedTimes[Math.floor(sortedTimes.length / 2)] || 0;

  let bestIdx = -1;
  for (let i = 0; i < laps.length; i += 1) {
    const t = laps[i].lapTime;
    if (!t || t <= 0) continue;
    if (medianTime > 0 && t < medianTime * 0.5) continue;
    if (medianLapDist > 0 && laps[i].data?.length) {
      const maxDist = Math.max(...laps[i].data.map((p) => p.dist || 0));
      if (maxDist < medianLapDist * 0.75) continue;
    }
    if (bestIdx === -1 || t < laps[bestIdx].lapTime) bestIdx = i;
  }
  return bestIdx >= 0 ? bestIdx : 0;
}

// The fastest valid lap object, or null.
export function getBestLap(laps) {
  if (!laps?.length) return null;
  return laps[findBestLapIndex(laps)] ?? null;
}
